import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, retryOperation, checkConnection } from '@/lib/supabase';
import { createUserProfile } from '@/lib/db';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, energyType: string) => Promise<void>;
  logout: () => Promise<void>;
  retryAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { language } = useLanguage();
  const MAX_RETRIES = 3;

  const initializeAuth = async () => {
    try {
      setError(null);
      
      // Check Supabase connection first
      const isConnected = await checkConnection();
      if (!isConnected) {
        throw new Error(language === 'fr' 
          ? 'Impossible de se connecter à la base de données. Veuillez vérifier votre connexion.' 
          : 'Unable to connect to the database. Please check your connection.');
      }

      const { data: { session }, error: sessionError } = await retryOperation(
        () => supabase.auth.getSession()
      );
      
      if (sessionError) {
        if (sessionError.message.includes('refresh_token_not_found')) {
          await supabase.auth.signOut();
          setCurrentUser(null);
          throw new Error(language === 'fr' 
            ? 'Session expirée. Veuillez vous reconnecter.' 
            : 'Session expired. Please log in again.');
        }
        throw sessionError;
      }
      
      setCurrentUser(session?.user ?? null);
    } catch (err: any) {
      console.error('Error initializing auth:', err);
      
      if (retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        setTimeout(initializeAuth, 2000);
      } else {
        setError(err.message || (language === 'fr' 
          ? 'Erreur de connexion à la base de données. Veuillez vérifier votre connexion.' 
          : 'Database connection error. Please check your connection.'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setCurrentUser(session?.user ?? null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const retryAuth = async () => {
    setLoading(true);
    setRetryCount(0);
    setError(null);
    await initializeAuth();
  };

  async function register(email: string, password: string, displayName: string, energyType: string) {
    try {
      setError(null);

      // 1. Check if user exists
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', email)
        .maybeSingle();

      if (existingUser) {
        throw new Error(language === 'fr'
          ? 'Un compte existe déjà avec cette adresse email'
          : 'An account already exists with this email address');
      }

      // 2. Create auth account
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
            energy_type: energyType,
          }
        },
      });

      if (signUpError) throw signUpError;

      if (!data.user) {
        throw new Error(language === 'fr'
          ? "Erreur lors de la création du compte"
          : "Error creating account");
      }

      // 3. Create user profile
      await createUserProfile(data.user.id, {
        email,
        displayName,
        energyType
      });

      return;

    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Handle "user already exists" error specifically
      if (error.message.includes('already exists')) {
        throw new Error(language === 'fr'
          ? 'Un compte existe déjà avec cette adresse email'
          : 'An account already exists with this email address');
      }
      
      throw error;
    }
  }

  async function login(email: string, password: string) {
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message === 'Invalid login credentials') {
          throw new Error(language === 'fr'
            ? 'Email ou mot de passe incorrect'
            : 'Invalid email or password');
        }
        throw error;
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || (language === 'fr'
        ? 'Erreur lors de la connexion'
        : 'Login error'));
    }
  }

  async function logout() {
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error: any) {
      console.error('Logout error:', error);
      throw new Error(error.message || (language === 'fr'
        ? 'Erreur lors de la déconnexion'
        : 'Logout error'));
    }
  }

  return (
    <AuthContext.Provider value={{ 
      currentUser, 
      loading, 
      error, 
      login, 
      register, 
      logout,
      retryAuth 
    }}>
      {error ? (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <div className="flex items-center gap-3 text-red-600 mb-4">
              <AlertCircle className="w-6 h-6" />
              <h2 className="text-xl font-semibold">Erreur de connexion</h2>
            </div>
            
            <p className="text-gray-600 mb-6">{error}</p>
            
            <div className="space-y-4">
              <Button 
                onClick={retryAuth}
                className="w-full bg-[#54075B] hover:bg-[#54075B]/90 flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {language === 'fr' ? 'Réessayer la connexion' : 'Retry connection'}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="w-full"
              >
                {language === 'fr' ? 'Recharger la page' : 'Reload page'}
              </Button>
            </div>

            <div className="mt-6 text-sm text-gray-500">
              <p className="font-medium mb-2">{language === 'fr' ? 'Suggestions :' : 'Suggestions:'}</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>{language === 'fr'
                  ? 'Vérifiez votre connexion Internet'
                  : 'Check your Internet connection'}</li>
                <li>{language === 'fr'
                  ? 'Assurez-vous d\'avoir cliqué sur "Connect to Supabase"'
                  : 'Make sure you clicked on "Connect to Supabase"'}</li>
                <li>{language === 'fr'
                  ? 'Essayez de vous reconnecter'
                  : 'Try logging in again'}</li>
              </ul>
            </div>
          </div>
        </div>
      ) : !loading && children}
    </AuthContext.Provider>
  );
}